import React, { Component } from "react";
import cloneDeep from "lodash/cloneDeep";
import Modal from "@cloudscape-design/components/modal";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Select from "@cloudscape-design/components/select";
import SpaceBetween from "@cloudscape-design/components/space-between";
import DatePicker from "@cloudscape-design/components/date-picker";
import Multiselect from "@cloudscape-design/components/multiselect";
import FormField from "@cloudscape-design/components/form-field";
import { useRecoilValue, useSetRecoilState } from "recoil";
import {
	tournamentsBannersAddModalStore,
} from "../stores/tournamentsBannersAddModal";
import { MAX_BANNER_BYTES } from "../stores/bannersAddModal";
import { tournamentsBannersStore } from "../stores/tournamentsBanners";
import { gameStore } from "../stores/games";

const MAX_VISIBLE_GAMES = 100;
const LIMIT_OPTIONS = [
	{ value: "0", label: "0 - All players" },
	{ value: "1", label: "1 - VIP players" },
];

const parseGameIds = (value) => {
	if (Array.isArray(value)) {
		return value
			.map((item) => (typeof item === "object" && item !== null ? item.value ?? item.gameID ?? item.id : item))
			.map((id) => parseInt(String(id).trim(), 10))
			.filter((id) => !isNaN(id) && id > 0);
	}

	return String(value || "")
		.split(",")
		.map((id) => parseInt(id.trim(), 10))
		.filter((id) => !isNaN(id) && id > 0);
};

const formatGameLabel = (gameName, gameId) => `${gameName || "Game"} #${gameId}`;

const buildActiveGameOptions = (games = []) =>
	games
		.filter((game) => Number(game?.active) === 1 && game?.gameID)
		.slice()
		.sort((left, right) => {
			const leftName = String(left?.name || "").toLowerCase();
			const rightName = String(right?.name || "").toLowerCase();

			return leftName.localeCompare(rightName, undefined, { numeric: true, sensitivity: "base" }) || Number(left.gameID) - Number(right.gameID);
		})
		.map((game) => ({
			value: game.gameID.toString(),
			label: formatGameLabel(game.name, game.gameID),
		}));

const isValidBannerFileSize = (file) => Boolean(file) && file.size <= MAX_BANNER_BYTES;

const getLimitOption = (value) => (Number(value) > 0 ? LIMIT_OPTIONS[1] : LIMIT_OPTIONS[0]);

class TournamentsBannersAddModal extends Component {

	constructor(props) {
		super(props);

		this.state = {
			loading: false,
			games: [],
			filteredGames: [],
		}
	}

	componentDidMount() {
		this.syncGameOptions();
	}

	componentDidUpdate(prevProps) {
		if (!prevProps.tournamentsBannersAddModal.show && this.props.tournamentsBannersAddModal.show) {
			this.resetFormFields();
		}

		if (prevProps.game.data !== this.props.game.data) {
			this.syncGameOptions();
		}
	}

	addBanner() {
		this.setState({
			loading: true
		});
		const body = {
			name: { text: this.props.tournamentsBannersAddModal.name, type: this.props.tournamentsBannersAddModal.type.value },
			badge: this.props.tournamentsBannersAddModal.badge,
			image: this.props.tournamentsBannersAddModal.image,
			limit: Number(this.props.tournamentsBannersAddModal.limit) > 0 ? 1 : 0,
			pool: this.props.tournamentsBannersAddModal.pool,
			gameID: this.props.tournamentsBannersAddModal.gameID ? parseGameIds(this.props.tournamentsBannersAddModal.gameID) : false,
			date: Math.floor(new Date(this.props.tournamentsBannersAddModal.date).getTime() / 1000)
		};
		this.props.Interface.request("set", {
			class: "data",
			module: "tournamentBanners",
			method: "add"
		}, { ...body }).then((result) => {
			this.setState({
				loading: false
			});
			this.props.Interface.addNoty("Success", "Tournaments banner was successfully added");
			this.props.setTournamentsBannersAddModal({ ...this.props.tournamentsBannersAddModal, show: false });
			this.props.Interface.loadTournamentsBannerList();
		}).catch((error) => {
			if (typeof error.text === "undefined") error = { text: error.toString() };
			this.props.setTournamentsBanners({ ...this.props.tournamentsBanners, loaded: true, error });

			this.setState({
				loading: false
			});

		})
	}

	resetFormFields = () => {
		this.setState({
			filteredGames: this.state.games.slice(0, MAX_VISIBLE_GAMES),
		});

		this.props.setTournamentsBannersAddModal({
			...this.props.tournamentsBannersAddModal,
			type: "",
			name: "",
			badge: "",
			gameID: "",
			pool: "",
			limit: "0",
			date: "",
			image: "",
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
		});
	};

	updateParam = (key, value) => {
		const updatedState = cloneDeep(this.props.tournamentsBannersAddModal);
		updatedState[key] = value;
		this.props.setTournamentsBannersAddModal(updatedState);
	};

	syncGameOptions = () => {
		const games = buildActiveGameOptions(this.props.game?.data || []);

		this.setState({
			games,
			filteredGames: games.slice(0, MAX_VISIBLE_GAMES),
		});
	};

	getSelectedGameOptions = () => {
		const selectedIds = parseGameIds(this.props.tournamentsBannersAddModal.gameID);

		return selectedIds.map((id) => this.state.games.find((option) => option.value === id.toString()) || {
			value: id.toString(),
			label: formatGameLabel("Game", id),
		});
	};

	handleGameSearch = (filteringText) => {
		const normalizedFilter = String(filteringText || "").trim().toLowerCase();
		const filteredGames = normalizedFilter.length === 0
			? this.state.games
			: this.state.games.filter((game) =>
				game.label.toLowerCase().includes(normalizedFilter) ||
				game.value.includes(normalizedFilter)
			);

		this.setState({
			filteredGames: filteredGames.slice(0, MAX_VISIBLE_GAMES),
		});
	};

	handleGameChange = ({ detail }) => {
		this.updateParam(
			"gameID",
			detail.selectedOptions.map((option) => option.value).join(", ")
		);
	};

	render() {
		const { tournamentsBannersAddModal, setTournamentsBannersAddModal } = this.props;
		const selectedGameOptions = this.getSelectedGameOptions();

		return (
			<Modal
				visible={tournamentsBannersAddModal.show}
				onDismiss={() =>
					setTournamentsBannersAddModal({ ...tournamentsBannersAddModal, show: false })
				}
				header="Add tournament banner"
				children={
					<SpaceBetween direction="vertical" size="m">
						<Box variant="span">Name Type</Box>
						<Select
							filteringType="auto"
							placeholder="Select name type"
							selectedOption={tournamentsBannersAddModal.type}
							onChange={(option) =>
								this.updateParam("type", option.detail.selectedOption)
							}
							options={tournamentsBannersAddModal.currentOption.type}
						/>
						<Box variant="span">Name</Box>
						<Input
							name="Title"
							autoComplete="off"
							inputMode="text"
							type="text"
							disableBrowserAutocorrect={true}
							placeholder="Name"
							value={tournamentsBannersAddModal.name}
							onChange={(event) =>
								setTournamentsBannersAddModal({
									...tournamentsBannersAddModal,
									name: event.detail.value,
								})
							}
						/>
						<Box variant="span">Badge</Box>
						<Input
							name="Badge"
							autoComplete="off"
							inputMode="text"
							type="text"
							disableBrowserAutocorrect={true}
							placeholder="Badge"
							value={tournamentsBannersAddModal.badge}
							onChange={(event) =>
								setTournamentsBannersAddModal({
									...tournamentsBannersAddModal,
									badge: event.detail.value,
								})
							}
						/>
						<Box variant="span">Image</Box>
						<input
							type="file"
							accept="image/jpg"
							onChange={(event) => {
								const file = event.target.files[0];
								if (file) {
									if (!isValidBannerFileSize(file)) {
										this.props.Interface.addNoty("Error", `Image size must not exceed ${MAX_BANNER_BYTES / 1024 / 1024} MB`);
										event.target.value = "";

										return;
									}

									const reader = new FileReader();
									reader.onload = (event) => {
										const result = event.target.result;
										this.updateParam("image", result);
									};
									reader.readAsDataURL(file);
								} else {
									this.props.Interface.addNoty("Error", "Please, select a valid image file");
								}
							}}
						/>
						<FormField
							label="Games"
							description="Select up to 100 visible active games; search by game name or ID"
							stretch
						>
							<Multiselect
								filteringType="manual"
								onLoadItems={({ detail }) => this.handleGameSearch(detail.filteringText)}
								selectedOptions={selectedGameOptions}
								onChange={this.handleGameChange}
								options={!this.props.game.loaded ? [] : this.state.filteredGames}
								statusType={!this.props.game.loaded ? "loading" : "finished"}
								disabled={this.state.games.length === 0}
								placeholder={!this.props.game.loaded ? "Loading games...": "Select games"}
							/>
						</FormField>
						<Box variant="span">Pool</Box>
						<Input
							name="Title"
							autoComplete="off"
							inputMode="number"
							type="number"
							disableBrowserAutocorrect={true}
							placeholder="Pool"
							value={tournamentsBannersAddModal.pool}
							onChange={(event) =>
								setTournamentsBannersAddModal({
									...tournamentsBannersAddModal,
									pool: event.detail.value,
								})
							}
						/>
						<Box variant="span">Limit</Box>
						<Select
							placeholder="Limit"
							selectedOption={getLimitOption(tournamentsBannersAddModal.limit)}
							onChange={(option) => this.updateParam("limit", option.detail.selectedOption.value)}
							options={LIMIT_OPTIONS}
						/>
						<Box variant="span">Date</Box>
						<DatePicker
							onChange={({ detail }) => setTournamentsBannersAddModal({
								...tournamentsBannersAddModal,
								date: detail.value,
							})}
							value={tournamentsBannersAddModal.date}
							openCalendarAriaLabel={selectedDate =>
								"Choose certificate expiry date" +
								(selectedDate
									? `, selected date is ${selectedDate}`
									: "")
							}
							placeholder="YYYY/MM/DD"
						 nextMonthAriaLabel={""} previousMonthAriaLabel={""} todayAriaLabel={""}/>
					</SpaceBetween>
				}
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="m">
							<Button
								variant="normal"
								onClick={() =>
									setTournamentsBannersAddModal({ ...tournamentsBannersAddModal, show: false })
								}
								children="Close"
							/>
							<Button
								variant="primary"
								disabled={this.state.loading}
								onClick={() => this.addBanner()}
								children="Ok"
							/>
						</SpaceBetween>
					</Box>
				}
			/>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const tournamentsBannersAddModal = useRecoilValue(tournamentsBannersAddModalStore);
		const setTournamentsBannersAddModal = useSetRecoilState(tournamentsBannersAddModalStore);
		const tournamentsBanners = useRecoilValue(tournamentsBannersStore);
		const setTournamentsBanners = useSetRecoilState(tournamentsBannersStore);
		const game = useRecoilValue(gameStore);
		return (
			<Component
				{...props}
				tournamentsBanners={tournamentsBanners}
				setTournamentsBanners={setTournamentsBanners}
				setTournamentsBannersAddModal={setTournamentsBannersAddModal}
				tournamentsBannersAddModal={tournamentsBannersAddModal}
				game={game}
			/>
		);
	};
}

export default withRecoil(TournamentsBannersAddModal);
